/**
 * Whether ONE item opened from the list gets the outcome-picker screen - a choice between a multi-parcel
 * consignment and splitting - instead of being taken straight into a splitting flow. Before this existed,
 * multi-parcel was only reachable from inside the item panel.
 *
 * A leaf module rather than a function beside ShowGroup in GroupingPanel.tsx: that file reaches
 * use-smartsend-api, whose dependency chain includes an untransformable ESM build, so nothing in it can
 * be imported by a test. This decision is worth testing directly, so it lives where it can be.
 */
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { FilterToSendableItems } from '../../components/smartsend/panels/FilterToSendableItems';
import { consignmentParcelBound } from './bounds';

/** Where the grouping panel was opened from - the list header/footer, or the item edit panel. */
export type GroupingPanelSource = 'ITEM-GROUP' | 'ITEM-PANEL';

export function ShowSingleItemOptions(source: GroupingPanelSource, items: SmartSendItemApiModel[]): boolean {
  // Restricted to the list on purpose: ConsignmentDetails builds a group of one, so the item panel would
  // otherwise show this radio screen immediately beside its own parcel-count dropdown - two controls
  // offering the same thing.
  if (source !== 'ITEM-GROUP' || items?.length !== 1) {
    return false;
  }

  if (!SettingsCache.settings.Features.includes('Consignments')) {
    return false;
  }

  const item = items[0];

  // Gated on what the allocator will actually build, matching the item panel's own picker - a single
  // order line pads across parcels, so even one qty-1 line yields a range. Gating on a unit count
  // instead (as Combining used to) denied from the list what the item panel offered.
  return (
    consignmentParcelBound((item?.MarketplaceOrderLines ?? []).filter(FilterToSendableItems), item?.Consignment) > 1
  );
}
